import { useEffect, useRef, useState } from "react";
import {
  isWithinInterval,
  startOfHour,
  endOfHour,
  isSameHour,
  startOfDay,
} from "date-fns";
import dayjs from "dayjs";
import { Tag, message, Modal } from "antd";
import TaskForm from "../../../task/TaskForm";

import { cn } from "../../../../lib/utils";

import { createTask } from "../../../../api/task";
import DayEquipmentTaskTile from "./DayEquipmentTaskTile";

// Generate time slots from 00:00 to 23:00
const hoursOfDay = Array.from(
  { length: 24 },
  (_, i) => `${i.toString().padStart(2, "0")}:00`
);

export default function DailyCalendar({
  date,
  equipments,
  tasks,
  onTaskUpdate,
}) {
  const [tasksList, setTasksList] = useState([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskFormInstance, setTaskFormInstance] = useState();
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [taskFormValues, setTaskFormValues] = useState(null);

  const containerRef = useRef(null)

  const populateTaskForm = () => {
    taskFormInstance?.setFieldsValue({
      equipment: { value: taskFormValues.equipment.value, label: taskFormValues.equipment.label },
      startDate: taskFormValues.startDate,
      dueDate: taskFormValues.dueDate,
    });
  }

  useEffect(() => {
    setTasksList(tasks?.filter((t) => t.isActive) || []);
  }, [tasks]);

  useEffect(() => {
    setShowTaskForm(!!selectedEquipment);
  }, [selectedEquipment]);

  // Scroll to current hour on initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const currentHour = dayjs().hour(); // Get current hour (0-23)
        const hourElement = containerRef.current.querySelector(
          `[data-hour-index="${currentHour}"]`
        );

        if (hourElement) {
          const containerWidth = containerRef.current.clientWidth;
          const elementOffset = hourElement.offsetLeft;
          const elementWidth = hourElement.offsetWidth;

          // const scrollPosition = elementOffset - (containerWidth / 2) + (elementWidth / 2); // To center the current hour
          const scrollPosition = elementOffset - elementWidth // To bring current hour at the start
          containerRef.current.scrollLeft = scrollPosition;
        }
      }
    }, 100); // Short delay

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative">
      <div className="flex gap-2 mb-2 justify-end">
        <Tag className="bg-sky-400 text-sky-700 border-sky-900">
          Task Allocated
        </Tag>
        <Tag className="bg-orange-400 text-orange-900 border-orange-900">
          Worklog Created
        </Tag>
        <Tag className="bg-green-400 text-green-900 border-green-900">
          Downloaded
        </Tag>
      </div>

      <div className="overflow-auto border rounded-md shadow-sm max-h-[600px]" ref={containerRef}>
        <div className="min-w-[2400px] border-collapse border border-gray-400">
          {/* Sticky Time Header Row */}
          <div className="grid grid-cols-[130px_repeat(24,_minmax(130px,_1fr))] sticky top-0 bg-white z-20 border-b border-gray-400">
            <div className="bg-white sticky left-0 border-r border-gray-400"></div>
            {hoursOfDay.map((hour, index) => (
              <div
                key={index}
                className={cn("p-4 border border-solid border-t-0 border-l-0 border-gray-300 text-start h-[68px]",
                  dayjs().hour() === index ? "bg-blue-100" : "bg-white"
                )}
                data-hour-index={index} // Used to calculate scroll position to center current hour
              >
                {hour}
              </div>
            ))}
          </div>

          {/* Equipment Rows */}
          {equipments?.length > 0 &&
            equipments
              ?.sort((a, b) => a.name.localeCompare(b.name))
              .map((e, index) => (
                <div
                  className="grid grid-cols-[130px_repeat(24,_minmax(130px,_1fr))] border-b border-gray-300"
                  key={index}
                >
                  {/* Sticky Equipment Name */}
                  <div className="h-full bg-white z-20 text-gray-500 sticky left-0 z-10 border-r border-gray-400 flex items-start p-2">
                    {e.name}
                  </div>

                  {/* Task Cells */}
                  {hoursOfDay.map((hour, i) => {
                    const currentHour = dayjs(date)
                      .startOf("day")
                      .add(i, "hour")
                      .toDate();

                    // Current cell's time boundaries
                    const cellStart = startOfHour(currentHour);
                    const cellEnd = endOfHour(currentHour);
                    const isStartOfDay = isSameHour(
                      currentHour,
                      startOfDay(date)
                    );

                    // Filtered tasks for currentHour (specific hour of the cell)
                    const relevantTasks = tasksList
                      ?.map((t) => {
                        const taskStart = dayjs(t.startDate);
                        const taskEnd = dayjs(t.dueDate);

                        const startsInThisCell = isWithinInterval(t.startDate, {
                          start: cellStart,
                          end: cellEnd,
                        });

                        const continuesFromPreviousDay =
                          isStartOfDay &&
                          taskStart.isBefore(cellStart) &&
                          taskEnd.isAfter(cellStart);

                        return {
                          task: t,
                          continuesFromPreviousDay,
                          startsInThisCell,
                        };
                      })
                      ?.filter(
                        ({
                          task,
                          continuesFromPreviousDay,
                          startsInThisCell,
                        }) => {
                          return (
                            task.equipment?.id === e.id &&
                            (startsInThisCell || continuesFromPreviousDay)
                          );
                        }
                      );

                    return (
                      <div
                        key={`${i}-${e.id}`}
                        className={
                          cn("relative z-1 cursor-pointer border border-solid border-t-0 border-l-0 border-gray-300 text-start ",
                            dayjs().hour() === i ? "bg-blue-100" : "bg-white"
                          )
                        }
                        onClick={() => {
                          // If user clicks on an empty cell, show modal to add task
                          if (!relevantTasks.length) {

                            setSelectedEquipment(e);
                            setTaskFormValues({
                              equipment: { value: e.id, label: e.name },
                              startDate: dayjs(currentHour),
                              dueDate: dayjs(currentHour).endOf("hour"),
                            })
                          }
                        }}
                      >
                        {relevantTasks.length > 0 ? (
                          relevantTasks.map(
                            ({ task, continuesFromPreviousDay }, idx) => {
                              return (
                                <DayEquipmentTaskTile
                                  task={task}
                                  date={date}
                                  currentHour={currentHour}
                                  onUpdate={async () => {
                                    await onTaskUpdate();
                                  }}
                                  continuesFromPreviousDay={
                                    continuesFromPreviousDay
                                  }
                                />
                              );
                            }
                          )
                        ) : (
                          <div className={"h-[89px]"}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
          <Modal
            title="Create Task"
            open={showTaskForm}
            onCancel={() => {
              setSelectedEquipment(null);
              setShowTaskForm(false);
            }}
            onOk={async () => {
              const { equipment, ...values } =
                await taskFormInstance?.validateFields();

              if (values) {
                try {
                  const res = await createTask({
                    ...values,
                    equipment: equipment.value,
                  });
                  if (res) {
                    message.success("Task created successfully");
                    setSelectedEquipment(null);
                    setShowTaskForm(false);
                    await onTaskUpdate();
                  }
                } catch (error) {
                  console.log(error);
                  message.error("Something went wrong");
                }
              }
            }}
          >
            <TaskForm
              onFormInstanceReady={(instance) => {
                setTaskFormInstance(instance);
                populateTaskForm()
              }}
            />
          </Modal>
        </div>
      </div>
    </div>
  );
}
